import type { GuidanceWeekRange } from './types';

/** Proxy timeline axis origin (matches weekAxisLabels). */
export const GUIDANCE_AXIS_BASE_DATE = new Date(2026, 5, 6);

/** Live week index of calendar today on ``GUIDANCE_AXIS_BASE_DATE`` (matches Kryspace). */
export function guidanceTodayWeek(now: Date = new Date()): number {
  const utc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const base = Date.UTC(
    GUIDANCE_AXIS_BASE_DATE.getFullYear(),
    GUIDANCE_AXIS_BASE_DATE.getMonth(),
    GUIDANCE_AXIS_BASE_DATE.getDate()
  );
  return (utc - base) / (7 * 86400000);
}

export const GUIDANCE_VIEWPORT_WEEK_MIN = 4;
export const GUIDANCE_VIEWPORT_WEEK_MAX = 52;
export const GUIDANCE_ZOOM_STEP_WEEKS = 2;

export interface GuidanceViewport {
  /** First visible week index on the timeline axis. */
  startWeek: number;
  /** Number of weeks shown in the fixed chart width. */
  weekCount: number;
  /** Fixed horizontal pixel width of the timeline (zoom adjusts week width, not this). */
  chartWidthPx: number;
}

export function viewportIncludesToday(
  viewport: GuidanceViewport,
  now: Date = new Date()
): boolean {
  const today = guidanceTodayWeek(now);
  return today >= viewport.startWeek && today < viewport.startWeek + viewport.weekCount;
}

/** Shift startWeek so calendar today falls inside the window. */
export function ensureViewportIncludesToday(
  viewport: GuidanceViewport,
  now: Date = new Date()
): GuidanceViewport {
  if (viewportIncludesToday(viewport, now)) return viewport;
  const today = Math.floor(guidanceTodayWeek(now));
  const lead = Math.min(2, Math.max(0, viewport.weekCount - 1));
  return { ...viewport, startWeek: Math.max(0, today - lead) };
}

export function defaultGuidanceViewport(
  modelWeekCount: number,
  modelWeekWidthPx: number,
  now: Date = new Date()
): GuidanceViewport {
  const weekCount = clampWeekCount(modelWeekCount);
  return ensureViewportIncludesToday(
    {
      startWeek: 0,
      weekCount,
      chartWidthPx: weekCount * modelWeekWidthPx,
    },
    now
  );
}

export interface GuidanceTimelineWindowInput {
  startWeek?: number;
  weekCount?: number;
  weekWidthPx?: number;
  chartWidthPx?: number;
}

export function guidanceViewportFromManifest(
  modelWeekCount: number,
  modelWeekWidthPx: number,
  window?: GuidanceTimelineWindowInput | null,
  now: Date = new Date()
): GuidanceViewport {
  if (window && typeof window.startWeek === 'number' && typeof window.weekCount === 'number') {
    const weekCount = clampWeekCount(window.weekCount);
    const weekWidthPx = window.weekWidthPx ?? modelWeekWidthPx;
    return ensureViewportIncludesToday(
      {
        startWeek: window.startWeek,
        weekCount,
        chartWidthPx: window.chartWidthPx ?? weekCount * weekWidthPx,
      },
      now
    );
  }
  return defaultGuidanceViewport(modelWeekCount, modelWeekWidthPx, now);
}

export function weekWidthForViewport(viewport: GuidanceViewport): number {
  return viewport.chartWidthPx / viewport.weekCount;
}

export function clampWeekCount(count: number): number {
  return Math.min(GUIDANCE_VIEWPORT_WEEK_MAX, Math.max(GUIDANCE_VIEWPORT_WEEK_MIN, count));
}

/** Zoom in (+): fewer weeks in the same pixel width → wider activity bars. */
export function adjustZoom(viewport: GuidanceViewport, zoomInSteps: number): GuidanceViewport {
  return {
    ...viewport,
    weekCount: clampWeekCount(viewport.weekCount - zoomInSteps * GUIDANCE_ZOOM_STEP_WEEKS),
  };
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function weekIndexToIsoDate(weekIndex: number): string {
  const d = new Date(GUIDANCE_AXIS_BASE_DATE.getTime() + weekIndex * 7 * 86400000);
  return d.toISOString().slice(0, 10);
}

export function weekIndexToDisplayDate(weekIndex: number): string {
  const d = new Date(GUIDANCE_AXIS_BASE_DATE.getTime() + weekIndex * 7 * 86400000);
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

export function isoDateToWeekIndex(iso: string): number {
  const d = new Date(`${iso}T12:00:00`);
  return Math.round((d.getTime() - GUIDANCE_AXIS_BASE_DATE.getTime()) / (7 * 86400000));
}

export function viewportFromDateRange(
  startIso: string,
  endIso: string,
  chartWidthPx: number
): GuidanceViewport | null {
  if (!startIso || !endIso) return null;
  const startWeek = isoDateToWeekIndex(startIso);
  const endWeek = isoDateToWeekIndex(endIso);
  if (endWeek < startWeek) return null;
  return {
    startWeek,
    weekCount: clampWeekCount(endWeek - startWeek + 1),
    chartWidthPx,
  };
}

export function barGeometryInViewport(
  range: GuidanceWeekRange,
  weekWidth: number,
  viewportStart: number
) {
  const left = (range.s - viewportStart) * weekWidth;
  const earlyWidth = (range.e - range.s) * weekWidth;
  const tailWidth = (range.l - range.e) * weekWidth;
  return { left, earlyWidth, tailWidth, totalWidth: earlyWidth + tailWidth };
}

/** Clip bar geometry to the visible chart viewport (non-negative left, trimmed width). */
export function clipBarGeometryInViewport(
  range: GuidanceWeekRange,
  weekWidth: number,
  viewportStart: number,
  viewportWeekCount: number
) {
  const raw = barGeometryInViewport(range, weekWidth, viewportStart);
  const chartWidth = viewportWeekCount * weekWidth;
  let { left, earlyWidth, tailWidth } = raw;

  if (left < 0) {
    const clipPx = -left;
    const total = earlyWidth + tailWidth;
    if (clipPx >= total) {
      return { left: 0, earlyWidth: 0, tailWidth: 0, totalWidth: 0 };
    }
    if (clipPx <= earlyWidth) {
      earlyWidth -= clipPx;
    } else {
      tailWidth -= clipPx - earlyWidth;
      earlyWidth = 0;
    }
    left = 0;
  }

  const maxWidth = chartWidth - left;
  const total = earlyWidth + tailWidth;
  if (total > maxWidth) {
    const overflow = total - maxWidth;
    if (overflow >= tailWidth) {
      earlyWidth = Math.max(0, earlyWidth - (overflow - tailWidth));
      tailWidth = 0;
    } else {
      tailWidth -= overflow;
    }
  }

  return {
    left,
    earlyWidth,
    tailWidth,
    totalWidth: earlyWidth + tailWidth,
  };
}

export function todayLinePx(todayWeek: number, viewport: GuidanceViewport): number | null {
  const weekWidth = weekWidthForViewport(viewport);
  const x = (todayWeek - viewport.startWeek) * weekWidth;
  if (x < 0 || x > viewport.chartWidthPx) return null;
  return x;
}
